import { Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { usePortalAuth, usePortalRecords } from "../../application/usePortalAuth";
import PortalVocabulary from "./PortalVocabulary";

function PortalRecordsUnavailable() {
  const { t } = useTranslation();
  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-sm text-content-tertiary">
        {t("portal.records.unavailable", "We could not load your panels just now.")}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm text-white focus-visible:outline-2 focus-visible:outline-brand-500"
      >
        {t("portal.records.retry", "Try again")}
      </button>
    </main>
  );
}

/**
 * Everything an authenticated portal route needs before it renders.
 *
 * Besides the sign-in check it settles which record the page is about. A
 * patient with one record never sees any of this: they go straight through,
 * exactly as before records could be linked.
 */
export default function PatientAuthGuard({ children }: { children: React.ReactNode }) {
  const { isPatient, loading } = usePortalAuth();
  const { records, loadingRecords, failedToLoad, activePatientId } =
    usePortalRecords(isPatient);

  if (loading) return null;
  if (!isPatient) return <Navigate to="/portal/login" replace />;

  // Decide nothing until the records are in. Rendering the chooser for a
  // single-record account, even for one frame, is a screen they must never see.
  if (loadingRecords) return null;

  // A listing that could not be read says nothing about how many panels exist.
  // Telling somebody with two professionals that they have none is worse than
  // saying the request failed.
  if (failedToLoad) return <PortalRecordsUnavailable />;

  if (records.length === 0) return <Navigate to="/portal/no-records" replace />;
  if (records.length > 1 && !activePatientId) {
    return <Navigate to="/portal/choose" replace />;
  }

  // Keyed on the record: switching panels unmounts and rebuilds the subtree,
  // so data a page holds in its own state goes with the cache rather than
  // rendering under the next record.
  return (
    <div key={activePatientId ?? "single"}>
      {/* The active panel's words, applied before its pages render. */}
      <PortalVocabulary />
      {children}
    </div>
  );
}
